import { apiClient, type ApiResponse } from './client';
import type { MockGRN, MockGRNItem } from '@/mocks/billing';

const BASE = '/api/v1/grn';

export interface CreateGRNItemPayload {
  itemId?: string;
  itemName: string;
  hsnCode?: string;
  quantity: number;
  unitName?: string;
  rate?: number;
  poId?: string | null;
  poNumber?: string | null;
  barcode?: string | null;
  companyBarcode?: string | null;
  batchNumber?: string | null;
  isAutoBatch?: boolean;
  mfgDate?: string | null;
  expiryDate?: string | null;
  supplierBatchNo?: string | null;
  serials?: any[];
}

export interface CreateGRNPayload {
  warehouseId: string;
  supplierId?: string | null;
  date?: string;
  notes?: string;
  items: CreateGRNItemPayload[];
}

export interface GRNApiItem {
  id: string;
  grnId: string;
  itemId: string | null;
  itemName: string | null;
  poId: string | null;
  poNumber: string | null;
  hsnCode: string | null;
  quantity: number;
  unitName: string | null;
  rate: number;
  total: number;
  barcode: string | null;
  companyBarcode: string | null;
  batchNumber?: string | null;
  mfgDate?: string | null;
  expiryDate?: string | null;
  supplierBatchNo?: string | null;
  requiresIncomingQC?: boolean;
  requires_incoming_qc?: boolean;
}

export interface GRNApiRecord {
  id: string;
  grnNumber: string;
  companyId: string;
  warehouseId: string;
  warehouseName: string | null;
  supplierId: string | null;
  supplierName: string | null;
  date: string;
  status: string;
  totalQty: number;
  totalValue: number;
  createdBy: string;
  createdByName?: string | null;
  createdAt: string;
  itemCount?: number;
  totalItemsQty?: number;
  unmatchedCount?: number;
  poLinkedCount?: number;
  items?: GRNApiItem[];
  // ✅ PI fields
  piCreated?: boolean;
  invoiceId?: string | null;
  linkedPINumber?: string | null;
  pi_number?: string | null;
  pi_created_date?: string | null;
  notes?: string | null;
}

export function mapGRNItemToMock(item: GRNApiItem): MockGRNItem {
  return {
    itemId: item.itemId || '',
    itemName: item.itemName || '',
    hsnCode: item.hsnCode || '',
    qty: item.quantity,
    unit: item.unitName || '',
    rate: item.rate,
    poRef: item.poNumber,
    batchNumber: item.batchNumber || (item as any).batch_number,
    mfgDate: item.mfgDate || (item as any).mfg_date || null,
    expiryDate: item.expiryDate || (item as any).expiry_date || null,
    serials: (item as any).serials || (item as any).serial_items || [],
    requiresIncomingQC: Boolean(item.requiresIncomingQC ?? item.requires_incoming_qc),
    // As entered when another unit than the stock unit was used (2 Ton @ ₹80,000)
    enteredQty: (item as any).enteredQty ?? null,
    enteredUnitName: (item as any).enteredUnitName ?? null,
    enteredRate: (item as any).enteredRate ?? null,
  };
}

// ✅ Type guard for GRN status
type GRNStatus = 'CONFIRMED' | 'INVOICED' | 'CANCELLED';

const isValidGRNStatus = (status: string): status is GRNStatus => {
  return ['CONFIRMED', 'INVOICED', 'CANCELLED'].includes(status);
};

export function mapGRNToMockGRN(
  record: GRNApiRecord,
  linkedPOs: string[] = [],
  notes?: string,
): MockGRN {
  const items = (record.items || []).map(mapGRNItemToMock);
  const totalItems = record.itemCount ?? items.length;
  const totalValue = Number(record.totalValue || 0);
  const isUuid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      record.createdBy || '',
    );
  const createdByDisplay =
    record.createdByName || (isUuid ? 'Unknown User' : record.createdBy);
  const resolvedLinkedPOs =
    linkedPOs.length > 0
      ? linkedPOs
      : Array.from(
          new Set(
            items
              .map((item) => item.poRef)
              .filter((po): po is string => Boolean(po)),
          ),
        );

  // ✅ Safe status
  const status = isValidGRNStatus(record.status) ? record.status : 'CONFIRMED';

  // ✅ Debug logs
  console.log('🔍 Mapping GRN:', record.grnNumber);
  console.log('🔍 piCreated from API:', record.piCreated);
  console.log('🔍 status from API:', record.status);
  console.log('🔍 pi_number from API:', record.pi_number);

  const primaryBatch = record.batch_number || record.batchNumber || (record.items && record.items[0] ? (record.items[0].batch_number || record.items[0].batchNumber) : null);

  return {
    id: record.id,
    grnNumber: record.grnNumber,
    supplierId: record.supplierId || '',
    supplierName: record.supplierName || '',
    date: record.date,
    createdAt: record.createdAt,
    warehouseId: record.warehouseId,
    warehouseName: record.warehouseName || '',
    createdBy: createdByDisplay,
    linkedPOs: resolvedLinkedPOs,
    items,
    totalItems,
    totalValue,
    piCreated: record.piCreated || false,
    // ✅ Correct
    linkedPINumber: record.linkedPINumber || record.pi_number || null,
    piCreatedDate: record.pi_created_date || null,
    batchNumber: primaryBatch || undefined,
    status: status, // ✅ Type safe
    notes: notes || record.notes || null,
  };
}

export interface GetGRNsParams {
  search?: string;
  status?: string;
  supplier_id?: string;
  warehouse_id?: string;
  from_date?: string;
  to_date?: string;
}

export async function apiCreateGRN(
  payload: CreateGRNPayload,
): Promise<ApiResponse<GRNApiRecord>> {
  const { data } = await apiClient.post<ApiResponse<GRNApiRecord>>(
    BASE,
    payload,
  );
  return data;
}

export async function apiGetAllGRNs(
  params: GetGRNsParams = {},
): Promise<ApiResponse<GRNApiRecord[]>> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.status) qs.set('status', params.status);
  if (params.supplier_id) qs.set('supplier_id', params.supplier_id);
  if (params.warehouse_id) qs.set('warehouse_id', params.warehouse_id);
  if (params.from_date) qs.set('from_date', params.from_date);
  if (params.to_date) qs.set('to_date', params.to_date);
  const query = qs.toString();
  const { data } = await apiClient.get<ApiResponse<GRNApiRecord[]>>(
    query ? `${BASE}?${query}` : BASE,
  );
  return data;
}

export async function apiGetGRNById(
  id: string,
): Promise<ApiResponse<GRNApiRecord>> {
  const { data } = await apiClient.get<ApiResponse<GRNApiRecord>>(
    `${BASE}/${id}`,
  );
  return data;
}
